import type { GradeLabel } from './grade-label';
import type { ImageQualityStatus } from './types';

export type KitStatus = 'DRAFT' | 'COMPLETE' | 'CANCELED';

export type StitchingJobStatus = 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';

export type KitImageStatus = 'CURRENT' | 'STALE';

export interface KitSaleMetadata {
  title?: string;
  description?: string;
  notes?: string;
  externalReference?: string;
  templateId?: string;
}

export interface KitCardSummary {
  cardId: string;
  shortCode?: string;
  sku?: string;
  identityDisplay?: string;
  gradeLabel?: GradeLabel;
  imageQualityStatus?: ImageQualityStatus;
}

export interface KitItem {
  cardId: string;
  position: number;
  frontImageKey: string;
  selectedAt: string;
  selectedBy: string;
  card: KitCardSummary;
}

export interface KitImage {
  imageId: string;
  jobId: string;
  kitId: string;
  s3Key: string;
  contentType: 'image/jpeg';
  width: number;
  height: number;
  status: KitImageStatus;
  patternId: string;
  sourceCardIds: readonly string[];
  createdAt: string;
  url?: string;
}

export interface KitStitchingJob {
  jobId: string;
  kitId: string;
  status: StitchingJobStatus;
  patternId: string;
  attempt: number;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  failedAt?: string;
  errorCode?: string;
  errorMessage?: string;
}

export interface KitSummary extends KitSaleMetadata {
  kitId: string;
  status: KitStatus;
  itemCount: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  completedAt?: string;
  completedBy?: string;
  reopenedAt?: string;
  reopenedBy?: string;
  canceledAt?: string;
  canceledBy?: string;
  currentPatternId?: string;
  currentJobId?: string;
}

export interface KitDetail extends KitSummary {
  items: readonly KitItem[];
  images: readonly KitImage[];
  jobs: readonly KitStitchingJob[];
}

export interface CreateKitRequest extends KitSaleMetadata {}

export interface UpdateKitRequest extends KitSaleMetadata {}

export interface AddKitItemsRequest {
  cardIds: readonly string[];
}

export interface ReorderKitItemsRequest {
  cardIds: readonly string[];
}

export type KitItemAddStatus =
  | 'added'
  | 'already-in-kit'
  | 'image-qa-required'
  | 'missing-front-image'
  | 'not-found'
  | 'pulled'
  | 'marketplace-committed'
  | 'reserved'
  | 'invalid';

export interface KitItemAddOutcome {
  cardId: string;
  status: KitItemAddStatus;
  message?: string;
}

export interface AddKitItemsResponse {
  kit: KitDetail;
  outcomes: readonly KitItemAddOutcome[];
}

export interface CompleteKitResponse {
  kit: KitDetail;
  job?: KitStitchingJob;
}

export interface ListKitsResponse {
  kits: readonly KitSummary[];
  nextCursor?: string;
}

export type KitPatternId = 'kit-5-fronts-v1' | 'kit-5-collage-v1' | 'kit-10-fronts-v1';

export interface KitPatternSlot {
  x: number;
  y: number;
  width: number;
  height: number;
  rotateDegrees?: number;
  zIndex?: number;
  borderWidth?: number;
  borderColor?: string;
  shadow?: boolean;
}

export interface KitStitchingPattern {
  patternId: KitPatternId;
  layout: 'grid' | 'collage';
  cardCount: 5 | 10;
  cardScale?: number;
  canvasWidth: 1600;
  canvasHeight: 1600;
  background: string;
  outputContentType: 'image/jpeg';
  outputQuality: number;
  slots: readonly KitPatternSlot[];
}

export type KitTemplateStatus = 'ACTIVE' | 'LEGACY' | 'DRAFT' | 'ARCHIVED';

export type KitTemplateSource = 'system' | 'operator';

export type KitTemplateBackground =
  | { kind: 'color'; color: string }
  | { kind: 'designed'; color: string; label: string }
  | {
      kind: 'image';
      s3Key: string;
      contentType: 'image/png' | 'image/jpeg';
      width: number;
      height: number;
      previewUrl?: string;
    };

export interface KitTemplate {
  templateId: string;
  patternId: string;
  name: string;
  status: KitTemplateStatus;
  source: KitTemplateSource;
  cardCount: KitSupportedCardCount;
  layout: KitStitchingPattern['layout'];
  cardScale?: number;
  canvasWidth: number;
  canvasHeight: number;
  background: KitTemplateBackground;
  outputContentType: KitStitchingPattern['outputContentType'];
  outputQuality: number;
  slots: readonly KitPatternSlot[];
  isDefault: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ListKitTemplatesResponse {
  templates: readonly KitTemplate[];
}

export interface CreateKitTemplateRequest {
  name: string;
  cardCount: KitSupportedCardCount;
  cardScale?: number;
  background: Extract<KitTemplateBackground, { kind: 'image' }>;
}

export interface CreateKitTemplateResponse {
  template: KitTemplate;
}

export interface ActivateKitTemplateResponse {
  template: KitTemplate;
}

export interface DeleteKitTemplateResponse {
  template: KitTemplate;
}

export interface CreateKitTemplateBackgroundUploadRequest {
  contentType: Extract<KitTemplateBackground, { kind: 'image' }>['contentType'];
  sizeBytes: number;
}

export interface CreateKitTemplateBackgroundUploadResponse {
  url: string;
  fields: Record<string, string>;
  key: string;
  expiresIn: number;
}

export const KIT_SUPPORTED_CARD_COUNTS = [5, 10] as const;

export type KitSupportedCardCount = (typeof KIT_SUPPORTED_CARD_COUNTS)[number];

export const KIT_IMAGE_WIDTH = 1600;
export const KIT_IMAGE_HEIGHT = 1600;
export const KIT_IMAGE_BACKGROUND = '#000000';
export const KIT_IMAGE_CONTENT_TYPE = 'image/jpeg';
export const KIT_IMAGE_JPEG_QUALITY = 90;
export const KIT_TEMPLATE_CARD_SCALE_MIN = 0.6;
export const KIT_TEMPLATE_CARD_SCALE_MAX = 1.1;
export const KIT_TEMPLATE_CARD_SCALE_STEP = 0.05;
export const KIT_COLLAGE_CARD_SCALE = 0.9;

function scaleSlotFromCenter(slot: KitPatternSlot, scale: number): KitPatternSlot {
  const width = Math.round(slot.width * scale);
  const height = Math.round(slot.height * scale);
  const centerX = slot.x + slot.width / 2;
  const centerY = slot.y + slot.height / 2;
  return {
    ...slot,
    x: Math.round(centerX - width / 2),
    y: Math.round(centerY - height / 2),
    width,
    height,
    borderWidth:
      slot.borderWidth === undefined
        ? undefined
        : Math.max(1, Math.round(slot.borderWidth * scale)),
  };
}

export function resolveKitTemplateCardScale(
  pattern: KitStitchingPattern,
  requestedScale?: number,
): number | undefined {
  if (pattern.cardScale === undefined) return undefined;
  if (requestedScale === undefined) return pattern.cardScale;
  const clamped = Math.min(
    KIT_TEMPLATE_CARD_SCALE_MAX,
    Math.max(KIT_TEMPLATE_CARD_SCALE_MIN, requestedScale),
  );
  return Math.round(clamped * 100) / 100;
}

export function resolveKitTemplateSlots(
  pattern: KitStitchingPattern,
  requestedScale?: number,
): readonly KitPatternSlot[] {
  const resolvedScale = resolveKitTemplateCardScale(pattern, requestedScale);
  if (resolvedScale === undefined || pattern.cardScale === undefined) return pattern.slots;
  const relativeScale = resolvedScale / pattern.cardScale;
  return pattern.slots.map((slot) => scaleSlotFromCenter(slot, relativeScale));
}

export const KIT_STITCHING_PATTERNS: readonly KitStitchingPattern[] = [
  {
    patternId: 'kit-5-collage-v1',
    layout: 'collage',
    cardCount: 5,
    cardScale: KIT_COLLAGE_CARD_SCALE,
    canvasWidth: KIT_IMAGE_WIDTH,
    canvasHeight: KIT_IMAGE_HEIGHT,
    background: '#f4e3d4',
    outputContentType: KIT_IMAGE_CONTENT_TYPE,
    outputQuality: KIT_IMAGE_JPEG_QUALITY,
    slots: [
      {
        x: 106,
        y: 170,
        width: 430,
        height: 602,
        rotateDegrees: -8,
        zIndex: 1,
        borderWidth: 8,
        borderColor: '#fff7ee',
        shadow: true,
      },
      {
        x: 558,
        y: 92,
        width: 470,
        height: 658,
        rotateDegrees: 4,
        zIndex: 4,
        borderWidth: 8,
        borderColor: '#fff7ee',
        shadow: true,
      },
      {
        x: 1048,
        y: 178,
        width: 430,
        height: 602,
        rotateDegrees: 8,
        zIndex: 2,
        borderWidth: 8,
        borderColor: '#fff7ee',
        shadow: true,
      },
      {
        x: 326,
        y: 770,
        width: 452,
        height: 632,
        rotateDegrees: 5,
        zIndex: 3,
        borderWidth: 8,
        borderColor: '#fff7ee',
        shadow: true,
      },
      {
        x: 822,
        y: 780,
        width: 452,
        height: 632,
        rotateDegrees: -5,
        zIndex: 5,
        borderWidth: 8,
        borderColor: '#fff7ee',
        shadow: true,
      },
    ].map((slot) => scaleSlotFromCenter(slot, KIT_COLLAGE_CARD_SCALE)),
  },
  {
    patternId: 'kit-5-fronts-v1',
    layout: 'grid',
    cardCount: 5,
    canvasWidth: KIT_IMAGE_WIDTH,
    canvasHeight: KIT_IMAGE_HEIGHT,
    background: KIT_IMAGE_BACKGROUND,
    outputContentType: KIT_IMAGE_CONTENT_TYPE,
    outputQuality: KIT_IMAGE_JPEG_QUALITY,
    slots: [
      { x: 161, y: 204, width: 426, height: 596 },
      { x: 587, y: 204, width: 426, height: 596 },
      { x: 1013, y: 204, width: 426, height: 596 },
      { x: 374, y: 800, width: 426, height: 596 },
      { x: 800, y: 800, width: 426, height: 596 },
    ],
  },
  {
    patternId: 'kit-10-fronts-v1',
    layout: 'grid',
    cardCount: 10,
    canvasWidth: KIT_IMAGE_WIDTH,
    canvasHeight: KIT_IMAGE_HEIGHT,
    background: KIT_IMAGE_BACKGROUND,
    outputContentType: KIT_IMAGE_CONTENT_TYPE,
    outputQuality: KIT_IMAGE_JPEG_QUALITY,
    slots: [
      { x: 110, y: 414, width: 276, height: 386 },
      { x: 386, y: 414, width: 276, height: 386 },
      { x: 662, y: 414, width: 276, height: 386 },
      { x: 938, y: 414, width: 276, height: 386 },
      { x: 1214, y: 414, width: 276, height: 386 },
      { x: 110, y: 800, width: 276, height: 386 },
      { x: 386, y: 800, width: 276, height: 386 },
      { x: 662, y: 800, width: 276, height: 386 },
      { x: 938, y: 800, width: 276, height: 386 },
      { x: 1214, y: 800, width: 276, height: 386 },
    ],
  },
];

export function selectKitStitchingPattern(cardCount: number): KitStitchingPattern | undefined {
  return KIT_STITCHING_PATTERNS.find((pattern) => pattern.cardCount === cardCount);
}

export function listSystemKitTemplates(): readonly KitTemplate[] {
  const defaultByCount = new Map<number, string>();
  for (const pattern of KIT_STITCHING_PATTERNS) {
    if (!defaultByCount.has(pattern.cardCount))
      defaultByCount.set(pattern.cardCount, pattern.patternId);
  }

  return KIT_STITCHING_PATTERNS.map((pattern) => ({
    templateId: pattern.patternId,
    patternId: pattern.patternId,
    name: systemTemplateName(pattern),
    status: pattern.patternId === 'kit-5-fronts-v1' ? 'LEGACY' : 'ACTIVE',
    source: 'system',
    cardCount: pattern.cardCount,
    layout: pattern.layout,
    cardScale: pattern.cardScale,
    canvasWidth: pattern.canvasWidth,
    canvasHeight: pattern.canvasHeight,
    background:
      pattern.layout === 'collage'
        ? { kind: 'designed', color: pattern.background, label: 'Warm collage background' }
        : { kind: 'color', color: pattern.background },
    outputContentType: pattern.outputContentType,
    outputQuality: pattern.outputQuality,
    slots: pattern.slots,
    isDefault: defaultByCount.get(pattern.cardCount) === pattern.patternId,
  }));
}

function systemTemplateName(pattern: KitStitchingPattern): string {
  switch (pattern.patternId) {
    case 'kit-5-collage-v1':
      return '5-card marketplace collage';
    case 'kit-5-fronts-v1':
      return '5-card legacy grid';
    case 'kit-10-fronts-v1':
      return '10-card grid';
  }
}

export function isSupportedKitCardCount(cardCount: number): cardCount is KitSupportedCardCount {
  return KIT_SUPPORTED_CARD_COUNTS.includes(cardCount as KitSupportedCardCount);
}

export function validateKitStitchingPattern(pattern: KitStitchingPattern): readonly string[] {
  const errors: string[] = [];

  if (pattern.slots.length !== pattern.cardCount) {
    errors.push(
      `slot count ${pattern.slots.length} does not match card count ${pattern.cardCount}`,
    );
  }

  pattern.slots.forEach((slot, index) => {
    if (slot.width <= 0 || slot.height <= 0) {
      errors.push(`slot ${index + 1} must have positive dimensions`);
    }
    if (slot.x < 0 || slot.y < 0) {
      errors.push(`slot ${index + 1} must not start outside the canvas`);
    }
    if (slot.x + slot.width > pattern.canvasWidth) {
      errors.push(`slot ${index + 1} exceeds canvas width`);
    }
    if (slot.y + slot.height > pattern.canvasHeight) {
      errors.push(`slot ${index + 1} exceeds canvas height`);
    }
  });

  return errors;
}
